// DOM UI: HUD, program bar, bot panel, reboot tab, modal, keyboard. Player-facing text uses the
// Robot Uprising vocabulary (src/data/terms.ts); internal ids keep their original names.
import { type Decimal, fmt } from '../core/decimal';
import { ARTIFACTS, ARTIFACT_BY_ID } from '../data/artifacts';
import { BALANCE } from '../data/balance';
import { CLASS_LABEL, TERMS } from '../data/terms';
import { STAGES_PER_BIOME } from '../data/biomes';

/** Orb colours for the Special buttons (mockup: red Slash, gold Rally, blue Toy Bomb, purple Hero Time). */
const SKILL_COLORS: Record<string, string> = {
  strike: 'radial-gradient(circle at 50% 60%, #4aa3ff, #1b4fb8)',
  warcry: 'radial-gradient(circle at 50% 60%, #ffd84a, #c98a12)',
  frenzy: 'radial-gradient(circle at 50% 60%, #b77bff, #5b2fb8)',
  midas: 'radial-gradient(circle at 50% 60%, #ff9ccf, #c2408a)',
  firesword: 'radial-gradient(circle at 50% 60%, #ff5a5a, #a11a2a)',
  deadly: 'radial-gradient(circle at 50% 60%, #6fe39a, #1f8f4a)',
};

const PANEL_TITLES: Record<string, string> = {
  heroes: 'My Guardians', skills: 'Specials', tree: 'Sticker Book', gear: 'Gear', pets: 'Pals', artifacts: 'Treasures', prestige: 'Rewind',
};

const levelUpLabel = (count: number) => (count > 1 ? `Level Up ×${count}` : 'Level Up');
const muteLabel = (muted: boolean) => (muted ? '🔇 Sound off' : '🔊 Sound on');
import { DIFFICULTIES, type DifficultyId } from '../data/difficulty';
import { biomeForStage, monsterName } from '../data/biomes';
import { GUARDIAN_ART } from '../data/heroArt';
import { HEROES, describeHeroSkill } from '../data/heroes';
import { SKILLS, describeSkill } from '../data/skills';
import { BRANCHES, TIER_REQUIREMENT, TREE, type BranchId } from '../data/skillTree';
import { describeNode, nodeBlocked, spentInBranch, totalSkillPoints } from '../game/skillTree';
import { EQUIPMENT_TUNING, RARITIES, SETS, SLOTS } from '../data/equipment';
import { describeItem, itemName, itemValue, setCounts, type Item } from '../game/equipment';
import { PETS, PET_TUNING } from '../data/pets';
import { describePet, petAttackMult } from '../game/pets';
import { describeEffect, riderValue } from '../game/artifacts';
import { nextSkill } from '../game/heroSkills';
import type { BuyMode, Game } from '../game/game';
import type { Renderer } from './renderer';

const $ = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;

interface Row {
  row: HTMLElement; portrait: HTMLElement; nm: HTMLElement; st: HTMLElement; ms: HTMLElement;
  btn: HTMLButtonElement; cost: HTMLElement; n: HTMLElement;
}


/** Shows the first `frac` of a painted bar fill: clipped, not squashed, so its rounded caps stay round. */
function reveal(el: HTMLElement, frac: number): void {
  const f = Math.max(0, Math.min(1, Number.isFinite(frac) ? frac : 0));
  el.style.clipPath = `inset(0 ${((1 - f) * 100).toFixed(2)}% 0 0 round 99px)`;
}

/** A Special's painted icon (public/art/special_<id>.png, Batch 2). */
function specialIcon(id: string): string {
  return `<img class="special-ic" src="art/special_${id}.png" alt="">`;
}

/** The Rewind moment: night sky and a clock spinning backwards over the reset, gone after its 2.8s fade. */
function playRewind(gain: Decimal): void {
  document.getElementById('rewind-fx')?.remove();
  const fx = document.createElement('div');
  fx.id = 'rewind-fx';
  fx.innerHTML = `<h2>${TERMS.prestige}</h2>
    <div class="rewind-clock" aria-hidden="true"><i class="hand hr"></i><i class="hand min"></i><i class="hub"></i></div>
    <p>+${fmt(gain)} Stardust</p>`;
  fx.addEventListener('animationend', (e) => { if (e.target === fx) fx.remove(); });
  document.body.append(fx);
}
export class UI {
  private buyMode: BuyMode = 1;
  private rows = new Map<string, Row>();
  private artifactRows = new Map<string, Row>();
  private skillRows = new Map<string, Row>();
  private nodeRows = new Map<string, Row>();
  private tierLabels = new Map<string, HTMLElement>();
  private branch: BranchId = 'knight';
  private gearDirty = true;
  private unseenGear = false;
  private petsDirty = true;
  private tab = 'heroes';
  private skillEls = new Map<string, HTMLButtonElement>();
  private modalOk: (() => void) | null = null;
  private tapped = false;

  constructor(private game: Game, private renderer: Renderer, private onChange: () => void,
    private onDifficulty: (d: DifficultyId) => void = () => {}) {
    this.buildSkills();
    this.buildPanel();
    this.buildTree();
    this.bindInput();
    this.bindEvents();
    this.tapped = game.state.lifetime.taps > 0;
    $('tap-hint').hidden = this.tapped;
    this.renderStage();
    this.refreshAll();
    // The panel floats over the battlefield: keep the HUD and the fight centred in the visible part.
    // Each landing coin bounces the coin pill (restarting its animation).
    const coinPill = document.querySelector('.pill.coins')!;
    this.renderer.onCoinLanded = () => { coinPill.classList.remove('bump'); void (coinPill as HTMLElement).offsetWidth; coinPill.classList.add('bump'); };
    const sync = () => this.syncPanelSpace();
    const ro = new ResizeObserver(sync);
    ro.observe($('panel')); ro.observe($('enemy-bar'));
    window.addEventListener('resize', sync);
    sync();
    setInterval(() => { this.refreshHud(); this.refreshSkills(); }, 100);
    setInterval(() => { this.refreshPanel(); this.refreshSkillList(); this.refreshTree(); this.refreshGear(); this.refreshPets(); this.refreshArtifacts(); this.refreshPrestige(); }, 250);
  }

  refreshAll(): void {
    this.refreshHud(); this.refreshSkills(); this.refreshPanel(); this.refreshSkillList(); this.refreshTree(); this.refreshArtifacts(); this.refreshPrestige();
    $('mute').textContent = muteLabel(this.game.state.settings.muted);
    $('fight-boss').hidden = !this.game.state.farming;
  }

  /** Width the floating panel covers on the right (0 in the stacked phone layout). */
  private syncPanelSpace(): void {
    const panel = $('panel');
    const floating = getComputedStyle(panel).position === 'absolute';
    const space = floating ? Math.max(0, window.innerWidth - panel.getBoundingClientRect().left + 6) : 0;
    document.documentElement.style.setProperty('--panel-space', `${space}px`);
    this.renderer.setRightInset(space);
    // Coins fly from the fight to the coin pill.
    const battle = $('battle').getBoundingClientRect(), pill = document.querySelector('.pill.coins .coin')!.getBoundingClientRect();
    this.renderer.setCoinTarget({ x: pill.left + pill.width / 2 - battle.left, y: pill.top + pill.height / 2 - battle.top });
    // Keep the monster under the enemy bar, leaving room for the Giant timer that appears beneath it.
    const bar = $('enemy-bar'), timer = $('boss-bar').hidden ? 26 : 0;
    this.renderer.setTopInset(bar.getBoundingClientRect().bottom - $('battle').getBoundingClientRect().top + timer + 4);
  }

  showModal(title: string, html: string, cancellable: boolean, onOk: (() => void) | null): void {
    $('modal-title').textContent = title;
    $('modal-body').innerHTML = html;
    $('modal-cancel').hidden = !cancellable;
    $('modal-ok').hidden = false;
    $('modal-ok').textContent = 'OK';
    this.modalOk = onOk;
    $('modal').hidden = false;
  }

  // ---- Build ------------------------------------------------------------------------

  private buildSkills(): void {
    const bar = $('skill-buttons');
    SKILLS.forEach((s, i) => {
      const b = document.createElement('button');
      b.className = 'skill';
      b.style.setProperty('--sc', SKILL_COLORS[s.id] ?? '#3b6cff');
      b.innerHTML = `<span class="kbd">${i + 1}</span><span class="cost">${s.manaCost}</span>
        <span class="orb"><span class="ic">${specialIcon(s.id)}</span><span class="cd"></span><span class="tm"></span></span><span class="lb">${s.name}</span>`;
      b.addEventListener('click', () => this.useSkill(i));
      bar.appendChild(b);
      this.skillEls.set(s.id, b);
      // Levelling rows in the Skills tab.
      this.makeRow(s.id, '#2b3a66', specialIcon(s.id), () => {
        if (this.game.levelSkill(s.id)) { this.refreshSkillList(); this.onChange(); }
      }, $('skill-list'), this.skillRows);
    });
  }

  private makeRow(key: string, color: string, label: string, onBuy: () => void,
    parent = $('tab-heroes'), into = this.rows, currency = '<span class="coin"></span>', portraitClass = ''): void {
    const row = document.createElement('div');
    row.className = 'row';
    row.innerHTML = `<div class="portrait ${portraitClass}" style="background:${color}">${label}</div>
      <div class="info"><div class="nm"></div><div class="st"></div><div class="ms"></div></div>
      <button class="buy"><div class="c">${currency}<span class="cost"></span></div><div class="n"></div></button>`;
    const q = <T extends HTMLElement>(sel: string) => row.querySelector(sel) as T;
    q<HTMLButtonElement>('.buy').addEventListener('click', onBuy);
    parent.appendChild(row);
    into.set(key, { row, portrait: q('.portrait'), nm: q('.nm'), st: q('.st'), ms: q('.ms'),
      btn: q('.buy'), cost: q('.cost'), n: q('.n') });
  }

  private buildPanel(): void {
    // Tuck's painted portrait over his card colour (the gradient shows while it loads or if it's missing).
    this.makeRow('tap', 'url(art/player_look3_portrait.png) center / cover, linear-gradient(#ffb36b, #ff7a45)', '', () => { if (this.game.buyTap(this.buyMode)) this.afterPurchase(); },
      $('tab-heroes'), this.rows, undefined, 'cls-hero');
    for (const h of HEROES) {
      this.makeRow(h.id, h.color, h.name[0]!, () => { if (this.game.buyHero(h.id, this.buyMode)) this.afterPurchase(); },
        $('tab-heroes'), this.rows, undefined, `cls-${h.type}`);
    }
  }

  private buildTree(): void {
    const tabs = $('branch-tabs');
    for (const b of BRANCHES) {
      const btn = document.createElement('button');
      btn.className = 'branch';
      btn.dataset.branch = b.id;
      btn.style.setProperty('--bc', b.color);
      btn.innerHTML = `${b.icon} ${b.name}<span class="bs"></span>`;
      btn.addEventListener('click', () => { this.branch = b.id; this.refreshTree(); });
      tabs.appendChild(btn);
    }
    const list = $('tree-list');
    for (const b of BRANCHES) {
      for (const tier of [1, 2, 3] as const) {
        const label = document.createElement('div');
        label.className = 'tier-label';
        list.appendChild(label);
        this.tierLabels.set(`${b.id}:${tier}`, label);
        for (const node of TREE.filter((n) => n.branch === b.id && n.tier === tier)) {
          this.makeRow(node.id, b.color, node.icon, () => {
            if (this.game.levelNode(node.id)) { this.refreshTree(); this.onChange(); }
          }, list, this.nodeRows, '');
          this.nodeRows.get(node.id)!.row.style.setProperty('--bc', b.color);
        }
      }
    }
    $('respec-btn').addEventListener('click', () => {
      this.showModal('Reset your Sticker Book?', 'All stickers are returned for free. You can place them again right away.', true, () => {
        this.game.respecTree();
        this.refreshTree();
        this.onChange();
      });
    });
  }

  /** Artifact rows are created lazily as artifacts are discovered (list stays in data order). */
  private ensureArtifactRows(): void {
    for (const def of ARTIFACTS) {
      if (this.artifactRows.has(def.id) || !(def.id in this.game.state.artifacts)) continue;
      this.makeRow(def.id, '#3a3558', def.icon, () => {
        if (this.game.levelArtifact(def.id, this.buyMode)) this.afterPurchase();
      }, $('artifact-list'), this.artifactRows, '<span class="relic-icon"></span>');
    }
    // Re-append in data order so newly discovered artifacts slot into place.
    for (const def of ARTIFACTS) {
      const r = this.artifactRows.get(def.id);
      if (r) $('artifact-list').appendChild(r.row);
    }
  }

  private discover(): void {
    const id = this.game.discoverArtifact();
    if (!id) return;
    const def = ARTIFACT_BY_ID.get(id)!;
    this.ensureArtifactRows();
    this.refreshArtifacts();
    this.showModal(`${def.icon} ${def.name}`, `You found a Treasure!<br>${describeEffect(def, 1)}`, false, null);
    this.onChange();
  }

  private afterPurchase(): void {
    this.refreshPanel();
    this.refreshArtifacts();
    this.onChange();
  }

  // ---- Input ------------------------------------------------------------------------

  private bindInput(): void {
    const canvas = $('canvas') as HTMLCanvasElement;
    canvas.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      const r = canvas.getBoundingClientRect();
      const pos = { x: e.clientX - r.left, y: e.clientY - r.top };
      // Grabbing a passing Supply Drop takes priority over a strike.
      if (this.renderer.hitSupplyDrop(pos)) {
        const at = this.renderer.supplyDropPos();
        if (this.game.meta.catchSupplyDrop()) { if (at) this.renderer.giftBurst({ x: at.x, y: at.y + 16 }); this.onChange(); return; }
      }
      this.tap(pos);
    });

    // Drop focus after clicks so Space/Enter never re-trigger the last button pressed.
    document.addEventListener('click', (e) => {
      const b = (e.target as HTMLElement).closest('button');
      if (b) b.blur();
    });

    window.addEventListener('keydown', (e) => {
      // Typing in a field, or any account/terms/intro screen in front: the keys are theirs, not the game's.
      const t = e.target as HTMLElement | null;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      const acct = document.getElementById('acct');
      if ((acct && !acct.hidden) || document.getElementById('story') || document.getElementById('gem-reward')) return;
      if (!$('overlay').hidden) return; // Phase 2 screens handle their own keys (Esc closes)
      if (!$('modal').hidden) {
        if (e.key === 'Enter') $('modal-ok').click();
        if (e.key === 'Escape') $('modal-cancel').click();
        return;
      }
      if (e.code === 'Space' || e.key === 'Enter') {
        e.preventDefault();
        if (!e.repeat) this.tap(this.renderer.randomMonsterPoint()); // no key-repeat macro tapping
      } else if (/^[1-9]$/.test(e.key) && Number(e.key) <= SKILLS.length) {
        this.useSkill(Number(e.key) - 1);
      } else if (e.key === 'b' || e.key === 'B') {
        this.fightBoss();
      }
    });

    $('fight-boss').addEventListener('click', () => this.fightBoss());

    const diffBtn = $('difficulty-btn');
    diffBtn.hidden = !import.meta.env.DEV; // tuning tool only; players always get GAME_DIFFICULTY
    diffBtn.textContent = this.game.difficulty.name;
    diffBtn.classList.add(this.game.difficulty.id);
    diffBtn.addEventListener('click', () => {
      const cur = this.game.difficulty.id;
      const choices = DIFFICULTIES.map((d) =>
        `<button class="diff-choice ${d.id === cur ? 'current' : ''}" data-diff="${d.id}"><b>${d.name}${d.id === cur ? ' (current)' : ''}</b><span>${d.blurb}</span></button>`).join('');
      this.showModal('Difficulty', `Each difficulty has its <b>own save</b>, so you can switch back and forth to compare.<div class="diff-choices">${choices}</div>`, true, null);
      $('modal-ok').hidden = true;
    });
    $('modal-body').addEventListener('click', (e) => {
      const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('button[data-diff]');
      if (!btn) return;
      const id = btn.dataset.diff as DifficultyId;
      $('modal').hidden = true;
      $('modal-ok').hidden = false;
      if (id !== this.game.difficulty.id) this.onDifficulty(id);
    });
    $('mute').addEventListener('click', () => {
      this.game.state.settings.muted = !this.game.state.settings.muted;
      $('mute').textContent = muteLabel(this.game.state.settings.muted);
      this.onChange();
    });
    $('settings-btn').addEventListener('click', (e) => { e.stopPropagation(); $('settings-pop').hidden = !$('settings-pop').hidden; });
    document.addEventListener('click', (e) => {
      if (!(e.target as HTMLElement).closest('#settings-pop')) $('settings-pop').hidden = true;
    });

    document.querySelectorAll<HTMLButtonElement>('#buy-mult button').forEach((b) => b.addEventListener('click', () => {
      this.buyMode = b.dataset.mult === 'max' ? 'max' : Number(b.dataset.mult);
      document.querySelectorAll('#buy-mult button').forEach((x) => x.classList.toggle('active', x === b));
      this.refreshPanel();
    }));

    document.querySelectorAll<HTMLButtonElement>('.tab').forEach((t) => t.addEventListener('click', () => {
      document.querySelectorAll('.tab').forEach((x) => x.classList.toggle('active', x === t));
      this.tab = t.dataset.tab!;
      for (const name of ['heroes', 'skills', 'tree', 'gear', 'pets', 'artifacts', 'prestige']) $(`tab-${name}`).hidden = this.tab !== name;
      if (this.tab === 'pets') this.petsDirty = true;
      if (this.tab === 'gear') { this.unseenGear = false; this.gearDirty = true; }
      $('buy-mult').hidden = !(this.tab === 'heroes' || this.tab === 'artifacts');
      $('panel-title').textContent = PANEL_TITLES[this.tab] ?? '';
      $('panel-sub').hidden = this.tab !== 'heroes';
      this.refreshPanel();
      this.refreshSkillList();
      this.refreshTree();
      this.refreshArtifacts();
    }));

    $('modal-ok').addEventListener('click', () => { $('modal').hidden = true; const f = this.modalOk; this.modalOk = null; f?.(); });
    $('modal-cancel').addEventListener('click', () => { $('modal').hidden = true; this.modalOk = null; });

    $('discover-btn').addEventListener('click', () => this.discover());

    $('pet-list').addEventListener('click', (e) => {
      const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('button[data-pet]');
      if (!btn) return;
      const id = btn.dataset.pet!;
      if (btn.dataset.act === 'level') this.game.levelPet(id);
      else this.game.setActivePet(id);
      this.petsDirty = true;
      this.onChange();
    });

    $('salvage-btn').addEventListener('click', () => {
      const n = this.game.salvageBelow('epic');
      if (n > 0) { this.renderer.banner(`Recycled ${n} gear pieces`, '#b8b3cc', 16); this.gearDirty = true; this.onChange(); }
    });
    $('gear-list').addEventListener('click', (e) => {
      const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('button[data-uid]');
      if (!btn) return;
      const uid = Number(btn.dataset.uid);
      if (btn.dataset.act === 'equip') this.game.equip(uid);
      else if (btn.dataset.act === 'salvage') this.game.salvage([uid]);
      this.gearDirty = true;
      this.onChange();
    });

    $('prestige-btn').addEventListener('click', () => {
      const gain = this.game.relicsOnPrestige();
      if (gain.lte(0)) return;
      const stars = Object.values(this.game.starsOnPrestige()).reduce((a, b) => a + b, 0);
      this.showModal('Rewind the night?', `Tuck starts again at stage 1 and earns <b>${fmt(gain)}</b> Stardust to spend on Treasures${stars ? `, plus <b>${stars} ★</b> Guardian Stars` : ''}.`, true, () => {
        playRewind(gain);
        this.game.prestige();
        this.onChange();
      });
    });
  }

  private fightBoss(): void {
    this.game.fightBoss();
    $('fight-boss').hidden = !this.game.state.farming;
  }

  private tap(pos: { x: number; y: number }): void {
    this.game.tap(pos);
    if (!this.tapped) { this.tapped = true; $('tap-hint').hidden = true; }
  }

  private useSkill(index: number): void {
    const s = SKILLS[index];
    if (!s) return;
    const blocked = this.game.skillBlocked(s.id);
    if (blocked === 'locked') this.renderer.banner(`${s.name} unlocks at stage ${s.unlockStage}`, '#fff', 18);
    else if (blocked === 'unlearned') this.renderer.banner(`Learn ${s.name} in the Specials tab`, '#fff', 18);
    else if (blocked === 'mana') this.renderer.banner('Not enough energy', '#8fb4ff', 18);
    else if (!blocked && this.game.useSkill(s.id)) this.onChange();
  }

  private bindEvents(): void {
    const e = this.game.events;
    e.on('spawn', () => this.renderKillDots());
    e.on('kill', () => this.renderKillDots());
    e.on('stageCleared', () => { this.renderStage(); $('fight-boss').hidden = true; });
    e.on('bossFailed', () => { $('fight-boss').hidden = false; });
    e.on('prestige', () => { this.renderStage(); this.refreshAll(); this.maybeRelicsIntro(); });
    e.on('petFood', (d) => {
      this.renderer.banner(`+${d.amount} ${d.pet.name} parts ${d.pet.icon}`, d.pet.color, 16);
      this.petsDirty = true;
    });
    e.on('petLeveled', () => { this.petsDirty = true; });
    e.on('itemDropped', (d) => {
      const r = RARITIES.find((x) => x.id === d.item.rarity)!;
      if (d.kept) this.renderer.banner(`${itemName(d.item)} dropped!`, r.color, 18);
      this.gearDirty = true;
      if (d.kept && this.tab !== 'gear') this.unseenGear = true;
      this.onChange();
    });
    e.on('offline', (o) => {
      const mins = Math.floor(o.seconds / 60);
      const span = mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins}m`;
      this.showModal('Welcome back!', `Your Guardians kept fighting for <b>${span}</b> and collected <b>${fmt(o.gold)}</b> coins.`, false, null);
    });
  }

  // ---- Refresh ------------------------------------------------------------------------

  /** Stage medallions (previous · current · next), each tinted with its room's colours. */
  renderStage(): void {
    const stage = this.game.state.stage;
    const zone = Math.floor((stage - 1) / STAGES_PER_BIOME) + 1;
    $('stage-label').textContent = `Zone ${zone}`;
    $('biome-label').textContent = biomeForStage(stage).name;
    const medal = (sel: string, n: number) => {
      const el = document.querySelector<HTMLElement>(`.medal.${sel}`)!;
      el.classList.toggle('none', n < 1);
      if (n >= 1) {
        const b = biomeForStage(n);
        el.style.setProperty('--sky', `linear-gradient(${b.sky[0]}, ${b.sky[1]})`);
        el.style.setProperty('--thumb', b.image ? `url(${b.image})` : 'none');
      }
      (el.querySelector('b') as HTMLElement).textContent = n >= 1 ? String(n) : '';
    };
    medal('prev', stage - 1); medal('cur', stage); medal('next', stage + 1);
    this.renderKillDots();
  }

  /** Dots between the current and next medallion fill as enemies fall; the next one glows red at the Giant. */
  private renderKillDots(): void {
    const g = this.game, m = g.monster;
    const boss = m.boss && !m.dead;
    let html = '';
    for (let i = 0; i < BALANCE.monstersPerStage; i++) html += `<i class="${boss || i < g.state.kills ? 'on' : ''}"></i>`;
    $('kill-dots').innerHTML = html;
    document.querySelector('.medal.next')!.classList.toggle('boss', boss);
    $('boss-bar').hidden = !boss;
    $('enemy-bar').classList.toggle('boss', boss);
    const b = biomeForStage(g.state.stage);
    $('enemy-name').textContent = monsterName(b, m);
    $('enemy-level').textContent = `Lv. ${g.state.stage}`;
    (document.querySelector('.eb-skull') as HTMLElement).innerHTML = m.boss ? '<i class="ic ic-giant"></i>' : '😈';
  }

  private refreshHud(): void {
    const g = this.game;
    $('gold').textContent = fmt(g.state.gold);
    $('tap-dmg').textContent = fmt(g.tapDamageNow());
    $('hero-dps').textContent = fmt(g.heroDpsNow());
    $('hud-dps').textContent = fmt(g.heroDpsNow());
    const m = g.monster;
    reveal($('enemy-fill'), m.dead ? 0 : m.hp.div(m.maxHp).toNumber());
    $('enemy-hp').textContent = `${fmt(m.hp.max(0))} / ${fmt(m.maxHp)} HP`;
    if (g.monster.boss && !g.monster.dead) {
      const total = g.bossTimeSec();
      reveal($('boss-fill'), g.bossTimeLeft / total);
      $('boss-time').textContent = Math.max(0, g.bossTimeLeft).toFixed(1) + 's';
    }
  }

  private refreshSkills(): void {
    const g = this.game;
    const max = g.maxMana();
    reveal($('mana-fill'), g.state.mana / max);
    $('mana-text').textContent = `${Math.floor(g.state.mana)} / ${max}`;
    for (const s of SKILLS) {
      const el = this.skillEls.get(s.id)!;
      const blocked = g.skillBlocked(s.id);
      const left = g.skillActiveLeft(s.id);
      el.classList.toggle('locked', blocked === 'locked' || blocked === 'unlearned');
      el.classList.toggle('active', blocked === 'active');
      el.classList.toggle('ready', blocked === null);
      el.classList.toggle('nomana', blocked === 'mana');
      // Overlay drains while the skill is active.
      const frac = blocked === 'active' && s.durationSec > 0 ? left / g.skillDurationSec(s) : blocked === 'locked' || blocked === 'unlearned' ? 1 : 0;
      (el.querySelector('.cd') as HTMLElement).style.transform = `scaleY(${frac})`;
      (el.querySelector('.tm') as HTMLElement).textContent =
        blocked === 'locked' ? `🔒${s.unlockStage}` : blocked === 'unlearned' ? 'Learn' : blocked === 'active' ? Math.ceil(left) + 's' : '';
      el.title = `${s.name} (Lv ${g.skillLevel(s.id)}): ${describeSkill(s.id, g.skillLevel(s.id))}. ${s.manaCost} energy.`;
    }
  }

  private refreshSkillList(): void {
    const g = this.game;
    let learnable = false;
    for (const s of SKILLS) {
      const r = this.skillRows.get(s.id)!, lvl = g.skillLevel(s.id);
      const cost = g.skillLevelCost(s.id);
      const stageReq = g.skillLevelStageReq(s.id);
      const stageOk = g.state.bestStage >= stageReq;
      const affordable = !!cost && stageOk && cost.lte(g.state.gold);
      if (affordable) learnable = true;
      if (this.tab !== 'skills') continue;
      r.nm.innerHTML = `${s.name} <span class="lv">${lvl > 0 ? `Lv ${lvl}` : 'not installed'}</span>`;
      r.st.textContent = lvl > 0 ? `${describeSkill(s.id, lvl)}${cost ? ` → next: ${describeSkill(s.id, lvl + 1)}` : ''}` : describeSkill(s.id, 1);
      r.ms.textContent = !cost ? 'Max level' : stageOk ? `${s.manaCost} energy${s.durationSec ? ` · ${Math.round(g.skillDurationSec(s))}s` : ''}`
        : `Reach stage ${stageReq} for the next level`;
      r.cost.textContent = cost ? fmt(cost) : '—';
      r.n.textContent = !cost ? 'Max' : lvl === 0 ? 'Learn' : `Level ${lvl + 1}`;
      r.btn.disabled = !affordable;
    }
    $('skill-dot').hidden = !learnable;
  }

  private refreshPanel(): void {
    const g = this.game;
    const t = this.rows.get('tap')!, tq = g.quoteTap(this.buyMode);
    t.nm.textContent = TERMS.player;
    t.st.innerHTML = `<span class="lvl">Lv. ${g.state.tapLevel}</span><span class="type hero">Hero</span>`;
    t.ms.innerHTML = `<span class="dps">${fmt(g.tapDamage())} Tap Damage</span> <span class="nx">→ ${fmt(g.tapDamage(g.state.tapLevel + tq.count))}</span>`;
    t.cost.textContent = fmt(tq.cost);
    t.n.textContent = levelUpLabel(tq.count);
    t.btn.disabled = !tq.affordable;

    // Owned heroes, the next hireable one, and one locked teaser.
    let revealed = 0;
    for (const h of HEROES) {
      const r = this.rows.get(h.id)!, lvl = g.heroLevel(h.id);
      const visible = lvl > 0 || revealed < 2;
      if (lvl === 0 && visible) revealed++;
      r.row.hidden = !visible;
      if (!visible) continue;
      const teaser = lvl === 0 && !g.heroUnlocked(h);
      r.row.classList.toggle('locked', teaser);
      // Painted portrait once unlocked; locked teasers stay a '?' so they don't spoil the art.
      const art = teaser ? '' : GUARDIAN_ART[h.id]?.portrait ?? '';
      if (r.portrait.dataset.art !== art) {
        r.portrait.dataset.art = art;
        r.portrait.style.background = art ? `url(${art}) center / cover, ${h.color}` : h.color;
      }
      r.portrait.textContent = teaser ? '?' : art ? '' : h.name[0]!;
      const q = g.quoteHero(h.id, this.buyMode);
      if (teaser) {
        r.nm.textContent = '???';
        r.st.textContent = 'Recruit the previous Guardian first';
        r.ms.textContent = '';
      } else {
        const gain = g.heroDpsOf(h, lvl + q.count).sub(g.heroDpsOf(h));
        const stars = g.heroStars(h.id);
        r.nm.textContent = h.name;
        r.st.innerHTML = `<span class="lvl">Lv. ${lvl}</span><span class="type ${h.type}">${CLASS_LABEL[h.type]}</span>`;
        // Stars ride on the portrait's corner, where a long name can't push them out of view.
        r.portrait.dataset.stars = stars ? `★${stars}` : '';
        r.portrait.title = stars ? `Guardian Stars: ×${fmt(g.starMult(h.id))} damage` : '';
        const next = (Math.floor(lvl / BALANCE.heroMilestoneEvery) + 1) * BALANCE.heroMilestoneEvery;
        const skill = nextSkill(h, lvl);
        const skillText = skill ? `Lv ${skill.level}: ${describeHeroSkill(h, skill.effect)}` : '';
        const star = g.nextStarLevel(h.id), starText = lvl >= star * 0.6 ? ` · ★ at Lv ${star} (on Rewind)` : '';
        const nextText = (lvl === 0 ? 'Fights automatically once recruited'
          : skill && skill.level <= next ? skillText
          : `x2 at Lv ${next}${skill ? ' · ' + skillText : ''}`) + starText;
        r.ms.innerHTML = `<span class="dps">${fmt(g.heroDpsOf(h))} DPS</span> <span class="nx">+${fmt(gain)}</span>`;
        r.row.title = nextText; // milestone / next trick on hover
        r.ms.dataset.next = nextText;
      }
      r.cost.textContent = fmt(q.cost);
      r.n.textContent = lvl === 0 ? 'Recruit' : levelUpLabel(q.count);
      r.btn.disabled = teaser || !q.affordable;
    }
  }

  private refreshPrestige(): void {
    const g = this.game, gain = g.relicsOnPrestige();
    $('relic-gain').textContent = fmt(gain);
    // Coins bought with purchased Gems this run multiply this Rewind's Stardust.
    const bonus = g.boughtCoinsBonus();
    $('bought-bonus').hidden = bonus <= 1.0001;
    $('bought-bonus').innerHTML = `Coins you bought this run: <b>×${bonus.toFixed(2)}</b> Stardust`;
    const starsNow = Object.values(g.starsOnPrestige()).reduce((a, b) => a + b, 0);
    $('stars-gain').hidden = starsNow === 0;
    $('stars-gain').innerHTML = `Guardian Stars you'll earn: <b>+${starsNow} ★</b> (each ×${BALANCE.stars.mult} damage for that Guardian, forever)`;
    $('prestige-req').textContent = g.state.stage < BALANCE.prestige.minStage ? `(Reach stage ${BALANCE.prestige.minStage} to Rewind.)` : '';
    ($('prestige-btn') as HTMLButtonElement).disabled = gain.lte(0);
    $('relic-owned').textContent = fmt(g.state.relics);
    $('best-stage').textContent = String(g.state.bestStage);
    $('ascensions').textContent = String(g.state.ascensions);
    const showRelics = g.state.relics.gt(0) || g.artifactsOwned() > 0;
    $('relic-hud').hidden = !showRelics;
    $('relics').textContent = fmt(g.state.relics);
  }

  private refreshTree(): void {
    const g = this.game, st = g.state;
    const sp = g.skillPoints();
    $('tree-dot').hidden = sp <= 0;
    if (this.tab !== 'tree') return;
    $('sp-available').textContent = String(sp);
    $('sp-total').textContent = String(totalSkillPoints(st));
    ($('respec-btn') as HTMLButtonElement).disabled = Object.keys(st.skillTree).length === 0;
    for (const btn of document.querySelectorAll<HTMLButtonElement>('#branch-tabs .branch')) {
      const id = btn.dataset.branch as BranchId;
      btn.classList.toggle('active', id === this.branch);
      (btn.querySelector('.bs') as HTMLElement).textContent = `${spentInBranch(st, id)} placed`;
    }
    const branch = BRANCHES.find((b) => b.id === this.branch)!;
    $('branch-blurb').textContent = branch.blurb;
    const spent = spentInBranch(st, this.branch);
    for (const [key, label] of this.tierLabels) {
      const [b, t] = key.split(':') as [BranchId, string];
      const tier = Number(t) as 1 | 2 | 3;
      label.hidden = b !== this.branch;
      const need = TIER_REQUIREMENT[tier];
      label.classList.toggle('closed', spent < need);
      label.textContent = `Row ${tier} · ${tier} sticker${tier > 1 ? 's' : ''} per level` + (spent < need ? ` · place ${need - spent} more on the ${branch.name} page to open` : '');
    }
    for (const node of TREE) {
      const r = this.nodeRows.get(node.id)!;
      r.row.hidden = node.branch !== this.branch;
      if (r.row.hidden) continue;
      const lvl = g.nodeLevel(node.id), block = nodeBlocked(st, node.id);
      r.row.classList.toggle('locked', block === 'tier');
      r.nm.innerHTML = `${node.name} <span class="lv">${lvl}/${node.maxLevel}</span>`;
      r.st.textContent = lvl > 0 ? describeNode(node, lvl) : describeNode(node, 1).replace(/^/, 'Level 1: ');
      r.ms.textContent = block === 'max' ? 'Maxed' : lvl > 0 ? `Next: ${describeNode(node, lvl + 1)}` : '';
      r.cost.textContent = block === 'max' ? '—' : `${node.tier} ⭐`;
      r.n.textContent = block === 'max' ? 'Max' : lvl === 0 ? 'Place' : 'Upgrade';
      r.btn.disabled = block !== null;
    }
  }

  /**
   * One-time pointer to the Relics tab: shown after the first ascension, and to returning players
   * whose relics stopped being a passive bonus when artifacts arrived.
   */
  maybeRelicsIntro(): void {
    const g = this.game, st = g.state;
    if (st.flags.relicsIntro || g.artifactsOwned() > 0 || g.discoveryCost().gt(st.relics)) return;
    // Don't stomp another popup (e.g. "Welcome back" offline gold); try again once it's closed.
    if (!$('modal').hidden) { setTimeout(() => this.maybeRelicsIntro(), 500); return; }
    st.flags.relicsIntro = true;
    this.onChange();
    this.showModal('✨ Spend your Stardust!',
      `You have <b>${fmt(st.relics)}</b> Stardust. It doesn't do anything on its own. Spend it in the <b>Treasures</b> tab to find <b>Treasures</b>: permanent bonuses you keep through every Rewind.`,
      false, () => (document.querySelector('.tab[data-tab="artifacts"]') as HTMLButtonElement).click());
    $('modal-ok').textContent = 'Open Treasures';
  }

  private refreshGear(): void {
    $('gear-dot').hidden = !this.unseenGear;
    if (this.tab !== 'gear' || !this.gearDirty) return;
    this.gearDirty = false;
    const g = this.game, eq = g.state.equipment;
    const byUid = new Map(eq.items.map((i) => [i.uid, i]));
    const rarity = (i: Item) => RARITIES.find((r) => r.id === i.rarity)!;

    $('gear-slots').innerHTML = SLOTS.map((slot) => {
      const uid = eq.equipped[slot.id];
      const item = uid !== undefined ? byUid.get(uid) : undefined;
      if (!item) return `<div class="gslot empty"><div class="gi">${slot.icon}</div>${slot.name}<div class="gv">empty</div></div>`;
      const r = rarity(item);
      return `<div class="gslot r-${r.id}" title="${itemName(item)}">
        <div class="gi">${slot.icon}</div><span style="color:${r.color}">${r.name}</span>
        <div class="gv">x${itemValue(item).toNumber().toFixed(2)}</div>${item.set ? `<div>${SETS.find((s) => s.id === item.set)!.name}</div>` : ''}</div>`;
    }).join('');

    const counts = setCounts(eq);
    $('gear-sets').innerHTML = SETS.map((s) => {
      const n = counts.get(s.id) ?? 0;
      return `<div class="setline ${n >= 3 ? 'on' : ''}"><span>${s.name} ${n}/5</span><span>3: ${s.desc3} · 5: ${s.desc5}</span></div>`;
    }).join('');

    $('gear-count').textContent = `${eq.items.length}/${EQUIPMENT_TUNING.inventoryCap}`;
    $('gear-empty').hidden = eq.items.length > 0;
    const slotOrder = new Map(SLOTS.map((s, i) => [s.id, i]));
    const sorted = [...eq.items].sort((a, b) => slotOrder.get(a.slot)! - slotOrder.get(b.slot)! || itemValue(b).cmp(itemValue(a)));
    $('gear-list').innerHTML = sorted.map((item) => {
      const r = rarity(item), slot = SLOTS.find((s) => s.id === item.slot)!;
      const equipped = g.isEquipped(item.uid);
      const cur = eq.equipped[item.slot] !== undefined ? byUid.get(eq.equipped[item.slot]!) : undefined;
      const better = !equipped && (!cur || itemValue(item).gt(itemValue(cur)));
      return `<div class="item r-${r.id}" style="--rc:${r.color}"><span class="ii">${slot.icon}</span>
        <div class="in"><div class="nm">${itemName(item)}</div>
        <div class="st">${describeItem(item)} · stage ${item.stage}${better ? ' · <span class="better">▲ upgrade</span>' : ''}</div></div>
        ${equipped ? '<button class="eq" disabled>Equipped</button>'
          : `<button data-act="equip" data-uid="${item.uid}">Equip</button><button class="eq" data-act="salvage" data-uid="${item.uid}" title="Recycle">✕</button>`}
      </div>`;
    }).join('');
  }

  private refreshPets(): void {
    const g = this.game;
    $('pet-dot').hidden = !PETS.some((p) => g.petFood(p.id) >= g.petNextCost(p.id));
    if (this.tab !== 'pets' || !this.petsDirty) return;
    this.petsDirty = false;
    const active = g.state.pets.active;
    $('pet-list').innerHTML = PETS.map((p) => {
      const lvl = g.petLevel(p.id), food = g.petFood(p.id), cost = g.petNextCost(p.id), isActive = active === p.id;
      const bonus = lvl > 0 ? describePet(p, lvl, isActive) : describePet(p, 1, false).replace(/^/, 'Lv 1: ');
      const attack = isActive ? `Attacks every ${PET_TUNING.tapsPerAttack} taps for ${petAttackMult(lvl)}x tap damage` : '';
      return `<div class="pet ${isActive ? 'active' : ''} ${lvl === 0 ? 'locked' : ''}" style="--pc:${p.color}">
        <div class="pi">${lvl > 0 ? p.icon : '❔'}</div>
        <div class="pn"><div class="nm">${lvl > 0 ? p.name : '???'}<span class="lv">${lvl > 0 ? `Lv ${lvl}` : ''}</span></div>
          <div class="st">${bonus}</div><div class="ms">${attack || `Food ${food}/${cost}`}</div></div>
        <div class="pb">
          <button data-act="level" data-pet="${p.id}" ${food >= cost ? '' : 'disabled'}>${lvl === 0 ? 'Hatch' : 'Feed'} (${cost})</button>
          ${lvl > 0 ? `<button class="${isActive ? 'sel' : ''}" data-act="active" data-pet="${p.id}" ${isActive ? 'disabled' : ''}>${isActive ? 'Active' : 'Set active'}</button>` : ''}
        </div></div>`;
    }).join('');
  }

  private refreshArtifacts(): void {
    const g = this.game;
    const canDiscover = !g.allArtifactsOwned() && g.discoveryCost().lte(g.state.relics);
    const anyUpgrade = [...this.artifactRows.keys()].some((id) => g.quoteArtifact(id, 1).affordable);
    $('artifact-dot').hidden = !(canDiscover || anyUpgrade);
    if (this.tab !== 'artifacts') return;

    this.ensureArtifactRows();
    $('relics-bank').textContent = fmt(g.state.relics);
    $('artifact-count').textContent = `${g.artifactsOwned()}/${ARTIFACTS.length}`;
    $('artifact-empty').hidden = g.artifactsOwned() > 0;
    const btn = $('discover-btn') as HTMLButtonElement;
    btn.hidden = g.allArtifactsOwned();
    btn.disabled = !canDiscover;
    $('discover-cost').textContent = fmt(g.discoveryCost());

    for (const [id, r] of this.artifactRows) {
      const def = ARTIFACT_BY_ID.get(id)!, lvl = g.artifactLevel(id);
      const q = g.quoteArtifact(id, this.buyMode);
      r.nm.innerHTML = `${def.name} <span class="lv">Lv ${fmt(lvl)}</span>`;
      r.st.textContent = `${describeEffect(def, lvl)} → ${describeEffect(def, lvl + q.count).replace(/^.*? (?=[x+-])/, '')}`;
      const rider = riderValue(def, lvl);
      r.ms.textContent = def.damagePerLevel > 0 ? `All damage x${rider.lt(1000) ? rider.toNumber().toFixed(2) : fmt(rider)}` : '';
      r.cost.textContent = fmt(q.cost);
      r.n.textContent = `Level +${fmt(q.count)}`;
      r.btn.disabled = !q.affordable;
    }
  }
}
